import * as React from "react";
import { Box } from "@mui/material";

// Plyr (JS + CSS) is only fetched once the player is about to scroll into view.
const SampleCollectionVideoPlayer = React.lazy(() => import("./SampleCollectionVideoPlayer"));

const POSTER_SRC = "/videos/probeentnahme-poster.jpg";
const POSTER_WIDTH = 540;
const POSTER_HEIGHT = 960;

// Start loading the player this far before it enters the viewport.
const NEAR_VIEWPORT_MARGIN = "200px";

// Same box for the placeholder and the player so swapping them never shifts the layout.
const frameSx = {
  width: "100%",
  maxWidth: 360,
  mx: "auto",
  aspectRatio: "9 / 16",
  borderRadius: 2,
  overflow: "hidden",
  backgroundColor: "#000",
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
};

function PosterPlaceholder({ alt }) {
  return (
    <Box
      component="img"
      src={POSTER_SRC}
      alt={alt}
      width={POSTER_WIDTH}
      height={POSTER_HEIGHT}
      loading="lazy"
      decoding="async"
      sx={{ display: "block", width: "100%", height: "100%", objectFit: "contain" }}
    />
  );
}

// Portrait "sample collection" instruction video. Nothing but the poster is downloaded until
// the user presses play (preload="none" inside the player).
function SampleCollectionVideo({ title, labels }) {
  const frameRef = React.useRef(null);
  const [nearViewport, setNearViewport] = React.useState(false);

  React.useEffect(() => {
    const frame = frameRef.current;
    if (!frame || nearViewport) return undefined;

    if (typeof IntersectionObserver === "undefined") {
      setNearViewport(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setNearViewport(true);
          observer.disconnect();
        }
      },
      { rootMargin: NEAR_VIEWPORT_MARGIN }
    );
    observer.observe(frame);
    return () => observer.disconnect();
  }, [nearViewport]);

  return (
    <Box ref={frameRef} sx={frameSx}>
      {nearViewport ? (
        <React.Suspense fallback={<PosterPlaceholder alt={title} />}>
          <SampleCollectionVideoPlayer labels={labels} />
        </React.Suspense>
      ) : (
        <PosterPlaceholder alt={title} />
      )}
    </Box>
  );
}

export default SampleCollectionVideo;
